import { useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SuiteExecutionDetailsPanels } from '@/features/executions/components/SuiteExecutionDetailsPanels';
import { ResultsFileTestExecutionInlineList } from '@/features/uploads/components/ResultsFileTestExecutionInlineList';
import { s } from '@/theme/tokens';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  Button,
  Chip,
  formatStatusLabel,
  Link,
  resolveStatusChipTone,
  Surface,
  Text,
} from '@/ui/primitives';
import type { BuildDto, SuiteExecutionDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type SuiteExecutionDetailTitle = {
  name: string;
  runNumber: number;
};

type SuiteExecutionDetailContentProps = {
  title: string | SuiteExecutionDetailTitle;
  execution: SuiteExecutionDto;
  build: BuildDto | null;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const SuiteExecutionDetailContent = ({
  title,
  execution,
  build,
  onDelete,
  deleteLoading = false,
}: SuiteExecutionDetailContentProps): ReactElement => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const handleDeleteConfirm = useCallback(() => {
    setDeleteOpen(false);
    if (onDelete) {
      void onDelete();
    }
  }, [onDelete]);
  const titleLabel = typeof title === 'string' ? title : title.name;
  const runNumberLabel = typeof title === 'string' ? null : `#${title.runNumber}`;
  const durationLabel =
    execution.durationMs !== null && execution.durationMs !== undefined
      ? formatDuration(execution.durationMs)
      : null;
  const suiteHref = execution.suiteSlug
    ? `/suites/${execution.suiteSlug}`
    : execution.suiteId
      ? `/suites/${execution.suiteId}`
      : null;

  return (
    <>
      <ConfirmDialog
        open={deleteOpen}
        title="Delete Suite Execution"
        description={
          <Stack gap="sm">
            <Text role="body" color="textSecondary">
              Deletion will remove all associated test executions.
            </Text>
            <Text role="body" color="textSecondary">
              This action cannot be undone.
            </Text>
          </Stack>
        }
        confirmLabel="Delete Suite Execution"
        confirmColor="error"
        confirmVariant="contained"
        loading={deleteLoading}
        onClose={() => {
          setDeleteOpen(false);
        }}
        onConfirm={handleDeleteConfirm}
      />
      <DetailPageTemplate
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: s('md'), minWidth: 0 }}>
            <DetailTitlePrefix label="Suite Execution" nowrap />
            {suiteHref ? (
              <Link component={RouterLink} to={suiteHref} underline="hover" color="inherit">
                <Text
                  role="sectionTitle"
                  component="span"
                  sx={{ minWidth: 0, maxWidth: '100%', whiteSpace: 'normal' }}
                >
                  {titleLabel}
                </Text>
              </Link>
            ) : (
              <Text
                role="sectionTitle"
                component="h1"
                sx={{ minWidth: 0, maxWidth: '100%', whiteSpace: 'normal' }}
              >
                {titleLabel}
              </Text>
            )}
            {runNumberLabel ? (
              <Text role="sectionTitle" component="span" color="textSecondary">
                {runNumberLabel}
              </Text>
            ) : null}
          </Box>
        }
        status={
          <>
            {execution.isLatest ? (
              <Chip label="Latest" tone="primary" transform="uppercase" size="small" />
            ) : null}
            <Chip
              label={formatStatusLabel(execution.status)}
              tone={resolveStatusChipTone(execution.status)}
              transform="uppercase"
              size="small"
            />
          </>
        }
        headerMeta={
          durationLabel ? (
            <Text role="bodyMuted" component="span" sx={{ whiteSpace: 'nowrap' }}>
              {durationLabel}
            </Text>
          ) : null
        }
        headerActions={
          onDelete ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="primary"
                size="md"
                color="error"
                onClick={() => {
                  setDeleteOpen(true);
                }}
                disabled={deleteLoading}
                sx={{ color: 'common.white', '&:hover': { color: 'common.white' } }}
              >
                Delete
              </Button>
            </Box>
          ) : null
        }
        metadata={<SuiteExecutionDetailsPanels execution={execution} build={build} />}
        primaryContent={
          <Section paddingY="md">
            <Surface padding="lg" border>
              <Stack gap="md">
                <ResultsFileTestExecutionInlineList
                  suiteExecutionId={execution.id}
                  resultsFileId={execution.resultsFileId ?? null}
                  projectId={execution.projectId}
                  organisationId={execution.organisationId}
                  heading="Tests"
                  disableIndent
                  showExpandCollapseControls
                  rowColumnGap={s('md')}
                />
              </Stack>
            </Surface>
          </Section>
        }
      />
    </>
  );
};
